import { useEffect, useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { ArrowLeft, ArrowRight, Quote } from "lucide-react";
import Reveal, { Eyebrow } from "@/components/Reveal";
import { TESTIMONIALS } from "@/config/site";

const initials = (name) =>
  name
    .split(/[&,.\s]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0])
    .join("")
    .toUpperCase();

export default function Testimonials() {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const reduced = useReducedMotion();
  const count = TESTIMONIALS.length;

  useEffect(() => {
    if (paused || reduced) return undefined;
    const t = setInterval(() => setIndex((v) => (v + 1) % count), 7000);
    return () => clearInterval(t);
  }, [paused, reduced, count]);

  const t = TESTIMONIALS[index];

  return (
    <section id="testimonials" className="bg-surface py-24 lg:py-32">
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        <div className="max-w-3xl">
          <Reveal>
            <Eyebrow>Testimonials</Eyebrow>
          </Reveal>
          <Reveal delay={0.08}>
            <h2 className="mt-5 font-cabinet text-4xl font-extrabold tracking-tighter sm:text-5xl">
              What clients say.
            </h2>
          </Reveal>
        </div>

        <Reveal delay={0.12}>
          <div
            className="relative mt-12 max-w-3xl"
            onMouseEnter={() => setPaused(true)}
            onMouseLeave={() => setPaused(false)}
            data-testid="testimonials-slider"
          >
            <Quote className="absolute -top-3 -left-2 h-10 w-10 text-accent/20" aria-hidden="true" />
            <div className="min-h-[220px] pl-8 sm:min-h-[190px]">
              <AnimatePresence mode="wait">
                <motion.blockquote
                  key={index}
                  initial={{ opacity: 0, y: 18 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -18 }}
                  transition={{ duration: 0.4, ease: "easeOut" }}
                  data-testid="testimonial-quote"
                >
                  <p className="text-xl font-medium leading-relaxed text-ink sm:text-2xl">
                    &ldquo;{t.quote}&rdquo;
                  </p>
                  <footer className="mt-7 flex items-center gap-3">
                    <span
                      className="grid h-11 w-11 place-items-center rounded-full bg-brand/10 font-cabinet text-sm font-bold text-brand"
                      aria-hidden="true"
                    >
                      {initials(t.name)}
                    </span>
                    <div>
                      <p className="text-sm font-bold text-ink">{t.name}</p>
                      <p className="text-xs text-slate-500">{t.meta}</p>
                    </div>
                  </footer>
                </motion.blockquote>
              </AnimatePresence>
            </div>

            <div className="mt-8 flex items-center gap-4 pl-8">
              <button
                onClick={() => setIndex((v) => (v - 1 + count) % count)}
                aria-label="Previous testimonial"
                data-testid="testimonial-prev"
                className="grid h-11 w-11 place-items-center rounded-full border border-ink/15 bg-white text-ink transition-colors hover:border-brand hover:text-brand"
              >
                <ArrowLeft className="h-4 w-4" aria-hidden="true" />
              </button>
              <button
                onClick={() => setIndex((v) => (v + 1) % count)}
                aria-label="Next testimonial"
                data-testid="testimonial-next"
                className="grid h-11 w-11 place-items-center rounded-full border border-ink/15 bg-white text-ink transition-colors hover:border-brand hover:text-brand"
              >
                <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </button>
              <div className="flex gap-2">
                {TESTIMONIALS.map((item, i) => (
                  <button
                    key={item.name}
                    onClick={() => setIndex(i)}
                    aria-label={`Go to testimonial ${i + 1}`}
                    data-testid={`testimonial-dot-${i}`}
                    className={`h-2 rounded-full transition-all duration-300 ${
                      i === index ? "w-6 bg-brand" : "w-2 bg-ink/15 hover:bg-ink/30"
                    }`}
                  />
                ))}
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
